import { apiClient, LoginResponse, RegisterResponse, UserProfile, getAuthHeaders } from './api';

export interface LoginRequest {
  email: string;
  password: string;
  // reCAPTCHA v2 token from the widget on the login page. Backend
  // verifies it against Google before checking the password.
  recaptcha_token?: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  full_name: string;
  company_name: string;
  contact_person: string;
  phone: string;
  // Mowilex-specific fields
  mowilex_customer_id?: string;
  territory: string;
  // Indonesian address fields
  address1: string;
  address2?: string;
  city: string;
  province: string;
  kecamatan: string;
  kelurahan: string;
  postal_code: string;
  // Legacy address field for backward compatibility
  address?: string;
  // Legacy captcha token (kept for back-compat; populated to the same
  // value as recaptcha_token when the new flow is used).
  captcha_token?: string;
  // reCAPTCHA v2 token from the widget on the register form.
  recaptcha_token?: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

export interface RefreshTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface ActivateAccountResponse {
  message: string;
  success: boolean;
}

export interface ForgotPasswordRequest {
  email: string;
  recaptcha_token: string;
}

export interface ForgotPasswordResponse {
  success: boolean;
  message: string;
}

export interface VerifyResetTokenResponse {
  valid: boolean;
  email?: string;
  expires_at?: string;
  attempts_remaining?: number;
  error?: string;
}

export interface ResetPasswordRequest {
  token: string;
  new_password: string;
  confirm_password: string;
}

export interface ResetPasswordResponse {
  success: boolean;
  message: string;
}

export interface ValidatePasswordRequest {
  password: string;
  email?: string;
}

export interface ValidatePasswordResponse {
  validation: {
    is_valid: boolean;
    errors: string[];
    score: number;
    strength: 'very_weak' | 'weak' | 'medium' | 'strong';
  };
}

export interface PasswordPolicyResponse {
  policy: {
    min_length: number;
    max_length: number;
    require_uppercase: boolean;
    require_lowercase: boolean;
    require_digit: boolean;
    require_special: boolean;
    special_chars: string;
    description: string;
  };
}

class AuthService {
  /**
   * Login user with email and password
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<LoginResponse>('/auth/login', credentials);
      
      // Store tokens in localStorage
      if (response.tokens) {
        localStorage.setItem('auth_token', response.tokens.access_token);
        localStorage.setItem('refresh_token', response.tokens.refresh_token);
        localStorage.setItem('token_expiry', (Date.now() + response.tokens.expires_in * 1000).toString());
        // Store refresh token expiry (90 days from login)
        localStorage.setItem('refresh_token_expiry', (Date.now() + 90 * 24 * 60 * 60 * 1000).toString());
      }
      
      return response;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  /**
   * Register new user account
   */
  async register(userData: RegisterRequest): Promise<RegisterResponse> {
    try {
      const response = await apiClient.post<RegisterResponse>('/auth/register', userData);
      return response;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  }

  /**
   * Preview an activation token without consuming it. Used by the
   * activation page to decide whether to render a "set password" form
   * (for backoffice-created contacts) or auto-activate (for self-signup
   * users who already have a password).
   */
  async checkActivationToken(token: string): Promise<{
    valid: boolean;
    error?: string;
    email?: string;
    full_name?: string;
    requires_password?: boolean;
    expires_at?: string;
    /** True when the link is used AND the user is already verified —
     *  treat as a happy path, not an error (likely the user came
     *  back to the activate URL after completing the flow). */
    already_activated?: boolean;
  }> {
    return apiClient.get(`/auth/activate/check?token=${encodeURIComponent(token)}`);
  }

  /**
   * Activate user account using magic link token. Pass `newPassword`
   * when the token belongs to a contact created via the backoffice
   * Contacts tab — those users were seeded with a throwaway password
   * and must set their own during activation.
   */
  async activateAccount(token: string, newPassword?: string): Promise<ActivateAccountResponse> {
    try {
      const body: Record<string, string> = { token };
      if (newPassword) body.new_password = newPassword;
      const response = await apiClient.post<ActivateAccountResponse>('/auth/activate', body);
      return response;
    } catch (error) {
      console.error('Account activation error:', error);
      throw error;
    }
  }

  /**
   * Get current user profile
   */
  async getUserProfile(): Promise<UserProfile> {
    try {
      const headers = getAuthHeaders();
      const response = await apiClient.get<UserProfile>('/auth/me', headers);
      return response;
    } catch (error) {
      console.error('Get user profile error:', error);
      throw error;
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(): Promise<RefreshTokenResponse> {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiClient.post<RefreshTokenResponse>('/auth/refresh', {
        refresh_token: refreshToken
      });
      
      // Update stored tokens
      localStorage.setItem('auth_token', response.access_token);
      localStorage.setItem('token_expiry', (Date.now() + response.expires_in * 1000).toString());
      
      return response;
    } catch (error) {
      console.error('Token refresh error:', error);
      // Clear invalid tokens
      this.logout();
      throw error;
    }
  }

  /**
   * Change user password
   */
  async changePassword(passwordData: ChangePasswordRequest): Promise<{ message: string }> {
    try {
      const headers = getAuthHeaders();
      const response = await apiClient.post<{ message: string }>('/auth/change-password', passwordData, headers);
      return response;
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  }

  /**
   * Logout user and clear stored tokens
   */
  logout(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expiry');
    localStorage.removeItem('refresh_token_expiry');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('auth_token');
    const expiry = localStorage.getItem('token_expiry');

    if (!token || !expiry) {
      return false;
    }

    // Check if refresh token has expired (90-day session ended)
    const refreshExpiry = localStorage.getItem('refresh_token_expiry');
    if (refreshExpiry && Date.now() >= parseInt(refreshExpiry, 10)) {
      this.logout();
      return false;
    }

    // Check if access token is expired
    const expiryTime = parseInt(expiry, 10);
    if (Date.now() >= expiryTime) {
      // Token is expired, try to refresh
      this.refreshToken().catch(() => {
        // Refresh failed, user needs to login again
        this.logout();
      });
      return false;
    }

    return true;
  }

  /**
   * Get stored access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  /**
   * Check if token is about to expire (within 5 minutes)
   */
  isTokenExpiringSoon(): boolean {
    const expiry = localStorage.getItem('token_expiry');
    if (!expiry) return false;

    const expiryTime = parseInt(expiry, 10);
    const fiveMinutesFromNow = Date.now() + (5 * 60 * 1000); // 5 minutes

    return fiveMinutesFromNow >= expiryTime;
  }

  /**
   * Request password reset link
   */
  async forgotPassword(request: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
    try {
      const response = await apiClient.post<ForgotPasswordResponse>('/auth/forgot-password', request);
      return response;
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error;
    }
  }

  /**
   * Verify reset token validity
   */
  async verifyResetToken(token: string): Promise<VerifyResetTokenResponse> {
    try {
      const response = await apiClient.get<VerifyResetTokenResponse>(`/auth/reset-password/verify/${token}`);
      return response;
    } catch (error) {
      console.error('Verify reset token error:', error);
      throw error;
    }
  }

  /**
   * Reset password using valid token
   */
  async resetPassword(request: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    try {
      const response = await apiClient.post<ResetPasswordResponse>('/auth/reset-password', request);
      return response;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  }

  /**
   * Validate password strength
   */
  async validatePassword(request: ValidatePasswordRequest): Promise<ValidatePasswordResponse> {
    try {
      const response = await apiClient.post<ValidatePasswordResponse>('/auth/validate-password', request);
      return response;
    } catch (error) {
      console.error('Validate password error:', error);
      throw error;
    }
  }

  /**
   * Get password policy
   */
  async getPasswordPolicy(): Promise<PasswordPolicyResponse> {
    try {
      const response = await apiClient.get<PasswordPolicyResponse>('/auth/password-policy');
      return response;
    } catch (error) {
      console.error('Get password policy error:', error);
      throw error;
    }
  }
}

// Create and export a singleton instance
export const authService = new AuthService();

// Convenience exports for direct usage
export const login = (request: LoginRequest) => authService.login(request);
export const logout = () => authService.logout();
export const register = (request: RegisterRequest) => authService.register(request);
export const activateAccount = (token: string) => authService.activateAccount(token);
export const forgotPassword = (request: ForgotPasswordRequest) => authService.forgotPassword(request);
export const verifyResetToken = (token: string) => authService.verifyResetToken(token);
export const resetPassword = (request: ResetPasswordRequest) => authService.resetPassword(request);
export const validatePassword = (request: ValidatePasswordRequest) => authService.validatePassword(request);
export const getPasswordPolicy = () => authService.getPasswordPolicy();